import React, { useState, useEffect } from "react";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Calendar as CalendarIcon, Clock, Video, ExternalLink } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    fetch(`${API}/events`, { credentials: "include" })
      .then(r => r.json())
      .then(setEvents)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const upcomingEvents = events.filter(e => new Date(e.scheduled_at) >= new Date());
  const pastEvents = events.filter(e => new Date(e.scheduled_at) < new Date());

  const eventDates = events.map(e => new Date(e.scheduled_at));
  const selectedEvents = events.filter(e => {
    const evtDate = new Date(e.scheduled_at);
    return evtDate.toDateString() === selectedDate.toDateString();
  });

  const EventCard = ({ event }) => {
    const date = new Date(event.scheduled_at);
    const isPast = date < new Date();
    return (
      <div className={`bg-white rounded-xl p-6 border ${isPast ? 'border-slate-100 opacity-75' : 'border-slate-200'} shadow-sm transition-all hover:shadow-md`} data-testid={`event-${event.event_id}`}>
        <div className="flex items-start gap-4">
          <div className={`w-14 h-14 rounded-xl flex flex-col items-center justify-center shrink-0 ${isPast ? 'bg-slate-100 text-slate-400' : 'bg-[#20D3C0]/10 text-[#193B6E]'}`}>
            <span className="text-xs font-medium">{date.toLocaleDateString('es-MX', { month: 'short' })}</span>
            <span className="text-lg font-bold">{date.getDate()}</span>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-[#193B6E] mb-1 truncate" style={{ fontFamily: 'Outfit' }}>{event.title}</h3>
            <p className="text-sm text-slate-500 mb-3">{event.description}</p>
            <div className="flex flex-wrap items-center gap-4 text-xs text-slate-400">
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}</span>
              <span className="flex items-center gap-1"><Video className="w-3 h-3" /> {event.duration_minutes} min</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${event.program_id === 'exacer' ? 'bg-blue-50 text-blue-600' : 'bg-purple-50 text-purple-600'}`}>
                {event.program_id === 'exacer' ? 'Exacer-COLBACH' : 'Acredita-Bach'}
              </span>
            </div>
          </div>
          {!isPast && (
            <a
              href={event.meet_link}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#20D3C0] text-[#112C54] hover:bg-[#1AB8A6] font-semibold rounded-full px-4 py-2 text-sm transition-all flex items-center gap-1 shrink-0"
              data-testid={`join-event-${event.event_id}`}
            >
              <ExternalLink className="w-4 h-4" /> Unirse
            </a>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="events-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <h1 className="text-3xl font-bold text-[#193B6E] mb-2" style={{ fontFamily: 'Outfit' }}>Clases en Vivo</h1>
        <p className="text-slate-500 mb-8">Sesiones de Google Meet programadas con profesores expertos</p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Calendar */}
          <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm h-fit" data-testid="events-calendar">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(d) => d && setSelectedDate(d)}
              modifiers={{ hasEvent: eventDates }}
              modifiersClassNames={{ hasEvent: "bg-[#20D3C0]/20 text-[#193B6E] font-bold" }}
              className="rounded-md"
            />
            {selectedEvents.length > 0 && (
              <div className="mt-4 border-t border-slate-100 pt-4">
                <p className="text-sm font-medium text-[#193B6E] mb-2">Eventos del dia:</p>
                {selectedEvents.map(e => (
                  <p key={e.event_id} className="text-xs text-slate-500 py-1">{e.title}</p>
                ))}
              </div>
            )}
          </div>

          {/* Events list */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="upcoming">
              <TabsList className="mb-6">
                <TabsTrigger value="upcoming" data-testid="tab-upcoming">Proximos ({upcomingEvents.length})</TabsTrigger>
                <TabsTrigger value="past" data-testid="tab-past">Pasados ({pastEvents.length})</TabsTrigger>
              </TabsList>
              <TabsContent value="upcoming">
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin w-8 h-8 border-4 border-[#20D3C0] border-t-transparent rounded-full"></div>
                  </div>
                ) : upcomingEvents.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
                    <CalendarIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-400">No hay eventos proximos programados</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {upcomingEvents.map(e => <EventCard key={e.event_id} event={e} />)}
                  </div>
                )}
              </TabsContent>
              <TabsContent value="past">
                {pastEvents.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
                    <p className="text-slate-400">No hay eventos pasados</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pastEvents.map(e => <EventCard key={e.event_id} event={e} />)}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}
